import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil,
  Lock,
} from "lucide-react";
import { useState } from "react";
import {useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { useGoBack } from "../../shared/hooks/goBack";
import { api } from "../../shared/api/client";
import {
  Button,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type { ProjectDetail } from "../../shared/types/api";
import { ProjectBaselinesTab } from "./ProjectBaselinesTab";
import { ProjectFormDrawer } from "./ProjectFormDrawer";
import { ProjectMembersTab } from "./ProjectMembersTab";
import { ProjectBugsTab } from "../bugs/ProjectBugsTab";
import { ProjectMeetingsTab } from "../meetings/ProjectMeetingsTab";
import { ProjectScopeTab } from "../change/ProjectScopeTab";
import { ProjectMilestonesTab } from "./ProjectMilestonesTab";
import { ProjectModulesTab } from "../modules/ProjectModulesTab";
import { ReleasesTab } from "../releases/ReleasesTab";
import { ProjectOverviewTab } from "./ProjectOverviewTab";
import { ProjectWorkTab } from "../workitems/ProjectWorkTab";
import { HEALTH_TONE, STATUS_LABELS, STATUS_TONE } from "./projectVocabulary";
import { usePublishCurrentProject } from "../../shared/hooks/currentProject";

type Tab = "overview" | "work" | "bugs" | "members" | "baselines" | "scope" | "milestones"
  | "releases" | "modules" | "meetings";

const TABS: { key: Tab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "work", label: "Work" },
  { key: "bugs", label: "Bugs" },
  { key: "members", label: "Members" },
  { key: "baselines", label: "Baselines" },
  { key: "scope", label: "Scope changes" },
  { key: "milestones", label: "Milestones" },
  { key: "meetings", label: "Meetings" },
  { key: "releases", label: "Releases" },
  { key: "modules", label: "Modules" },
];

/**
 * A project and everything committed about it (§113).
 *
 * <p>Tabs rather than one long page: the four questions — how is it doing, who is on it, what did
 * we agree, what are the dated commitments — are asked separately and answered separately.
 */
export function ProjectDetailPage() {
  const { projectId = "" } = useParams();
  const { goBack, hasHistory } = useGoBack("/projects");
  const { can } = useAuth();
  /*
   * The tab lives in the URL, not in this component, so Back from an item returns to the tab it
   * was opened from — with its filters, which live there too. Held in state it reset to Overview
   * every time, which is why the meeting page had to link to ?tab=meetings to get back.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab: Tab = TABS.some((t) => t.key === requested) ? (requested as Tab) : "overview";

  /* Switching tabs drops the parameters, because they belong to the list being left: a status
     chosen on Work means something else on Bugs and nothing at all on Members. Replaces rather
     than pushes, so Back leaves the project instead of walking the tabs in reverse. */
  const setTab = (next: Tab) =>
    setSearchParams(next === "overview" ? {} : { tab: next }, { replace: true });
  const [editing, setEditing] = useState(false);

  const project = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => api.get<ProjectDetail>(`/api/v1/projects/${projectId}`),
  });

  // Tells the shell's create button which project it is standing in.
  usePublishCurrentProject(projectId, project.data?.name);

  if (project.isPending) {
    return <LoadingState label="Loading project…" />;
  }
  if (project.isError) {
    return <ErrorState error={project.error} onRetry={() => void project.refetch()} />;
  }

  const reload = () => void project.refetch();

  return (
    <>
      {/* The name and the sentence under it are said in the top bar, so what is left is one strip:
          the way back, what the project is, and the button that changes it. Three rows of chrome
          above the tabs was a band of empty page between the reader and what they came for. */}
      <PageHeader
        title={project.data.name}
        subtitle={project.data.description ?? undefined}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-ink-500">
        {/* Back to the list as it was left — its search, its status, its page. Arriving from
            outside has nothing behind it, so that lands on the list itself. */}
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          className="-ml-2"
          onClick={goBack}
        >
          {hasHistory ? "Back" : "All projects"}
        </Button>
        <span className="rounded-md bg-ink-100 px-2 py-0.5 font-mono text-xs text-ink-600">
          {project.data.projectKey}
        </span>
        <StatusBadge tone={STATUS_TONE[project.data.status]}>
          {STATUS_LABELS[project.data.status]}
        </StatusBadge>
        <StatusBadge tone={HEALTH_TONE[project.data.health]}>{project.data.health}</StatusBadge>
        {/* Said on the page rather than only in the edit form. Somebody about to paste a link
            into a channel should be able to see that most of the channel cannot open it. */}
        {project.data.isPrivate && (
          <StatusBadge tone="violet">
            <span className="inline-flex items-center gap-1">
              <Lock className="h-3 w-3" />
              Private
            </span>
          </StatusBadge>
        )}
        {project.data.healthOverride && (
          // Worth flagging: a manual colour on a portfolio report should never be mistaken for
          // one the numbers produced.
          <span
            className="text-xs text-amber-600"
            title={project.data.healthOverrideReason ?? undefined}
          >
            set manually
          </span>
        )}
        <span>{project.data.projectManagerName ?? "No manager assigned"}</span>
        {can("PROJECT_EDIT") && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Pencil className="h-4 w-4" />}
            className="ml-auto"
            onClick={() => setEditing(true)}
          >
            Edit project
          </Button>
        )}
      </div>

      <div className="mb-5 flex gap-1 border-b border-ink-200">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            className={cn(
              "-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              tab === entry.key
                ? "border-brand-600 text-brand-700"
                : "border-transparent text-ink-500 hover:text-ink-800",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <ProjectOverviewTab project={project.data} onChanged={reload} />}
      {tab === "work" && <ProjectWorkTab projectId={projectId} />}
      {tab === "members" && <ProjectMembersTab projectId={projectId} />}
      {tab === "baselines" && (
        <ProjectBaselinesTab project={project.data} onChanged={reload} />
      )}
      {tab === "scope" && <ProjectScopeTab projectId={projectId} />}
      {tab === "milestones" && <ProjectMilestonesTab projectId={projectId} />}
      {tab === "bugs" && <ProjectBugsTab projectId={projectId} />}
      {tab === "meetings" && <ProjectMeetingsTab projectId={projectId} />}
      {tab === "releases" && <ReleasesTab projectId={projectId} />}
      {tab === "modules" && <ProjectModulesTab projectId={projectId} />}

      <ProjectFormDrawer
        project={editing ? project.data : undefined}
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={reload}
      />
    </>
  );
}
